/**
 * PlayerInfoPanel (Shared)
 * Shows both players' info (color indicator, turn status, augmentations)
 * with augmentation summary bar and click-to-detail support.
 * Used by both SingleScreen and Multiplayer wrappers.
 */

import type {
  Augmentation,
  ChessState,
  Color,
} from "@cloudnest/augmented-chess-engine";
import { getAugmentation } from "@cloudnest/augmented-chess-engine";

interface PlayerInfoPanelProps {
  gameState: ChessState;
  onAugmentationClick: (augmentation: Augmentation) => void;
}

const SUMMARY_MAX_LENGTH = 96;

function getSummaryText(description: string): string {
  const firstLine = description.split("\n")[0] ?? description;
  const normalized = firstLine.replace(/\s+/g, " ").trim();
  if (normalized.length <= SUMMARY_MAX_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, SUMMARY_MAX_LENGTH - 3)}...`;
}

function PlayerSection({
  color,
  label,
  isCurrentTurn,
  augmentationIds,
  onAugmentationClick,
}: {
  color: Color;
  label: string;
  isCurrentTurn: boolean;
  augmentationIds: readonly string[];
  onAugmentationClick: (augmentation: Augmentation) => void;
}) {
  const colorIcon = color === "WHITE" ? "\u2654\uFE0E" : "\u265A\uFE0E";
  const headerClass =
    color === "WHITE" ? "chess-panel-header-white" : "chess-panel-header-black";
  const bgClass =
    color === "WHITE"
      ? "chess-player-indicator-white"
      : "chess-player-indicator-black";

  const augmentations = augmentationIds
    .map((id) => getAugmentation(id))
    .filter((aug): aug is Augmentation => aug !== undefined);
  const totalCount = augmentations.length;
  const latestAugmentationId =
    augmentationIds.length > 0
      ? augmentationIds[augmentationIds.length - 1]
      : null;

  return (
    <div className={`chess-panel-section ${bgClass} landscape:p-2`}>
      <h3 className={`chess-panel-header ${headerClass}`} style={{ margin: 0 }}>
        <span style={{ fontSize: "1.2rem" }}>{colorIcon}</span>
        {label}
        <span
          style={{
            marginLeft: 4,
            borderRadius: 999,
            backgroundColor: "var(--cn-chess-move-dot)",
            color: "var(--cn-chess-piece-black)",
            padding: "1px 6px",
            fontSize: "0.65rem",
            fontWeight: 700,
          }}
        >
          {"\uB204\uC801"} {totalCount}
        </span>
        {isCurrentTurn && (
          <span
            style={{
              marginLeft: 8,
              fontSize: "0.68rem",
              color: "var(--chess-status-success)",
              fontWeight: "normal",
            }}
          >
            ({"\uCC28\uB840"})
          </span>
        )}
      </h3>

      {augmentations.length > 0 ? (
        <ul className="chess-panel-list" style={{ marginTop: 6 }}>
          {augmentations.map((aug) => (
            <li
              key={aug.id}
              className="chess-panel-item"
              style={{ cursor: "pointer" }}
            >
              <button
                type="button"
                onClick={() => onAugmentationClick(aug)}
                className="chess-panel-item-button"
              >
                <div className="flex items-center gap-2">
                  <span className="chess-panel-item-name">
                    {aug.nameKo ?? aug.name}
                  </span>
                  {latestAugmentationId === aug.id && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-[0.65rem] text-blue-700">
                      {"\uCD5C\uC2E0"}
                    </span>
                  )}
                </div>
                <span className="chess-panel-item-summary">
                  {getSummaryText(aug.descriptionKo ?? aug.description)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="chess-panel-empty" style={{ marginTop: 8 }}>
          {"\uC99D\uAC15 \uC5C6\uC74C"}
        </p>
      )}
    </div>
  );
}

export function PlayerInfoPanel({
  gameState,
  onAugmentationClick,
}: PlayerInfoPanelProps): JSX.Element {
  const isWhiteTurn = gameState.currentPlayer === "WHITE";
  const whiteCount = gameState.augmentations?.white?.active?.length ?? 0;
  const blackCount = gameState.augmentations?.black?.active?.length ?? 0;
  const totalSelectionActions = gameState.actionHistory.filter(
    (action) => action.type === "SELECT_AUGMENTATION",
  ).length;
  const completedRounds = Math.floor(totalSelectionActions / 2);
  const roundLabel =
    gameState.phase === "AUGMENTATION_SELECTION"
      ? `\uC99D\uAC15 \uB77C\uC6B4\uB4DC ${completedRounds + 1} \uC9C4\uD589 \uC911`
      : `\uC644\uB8CC\uB41C \uC99D\uAC15 \uB77C\uC6B4\uB4DC ${completedRounds}\uD68C`;

  return (
    <div className="flex w-full flex-1 flex-col gap-2 landscape:min-h-0">
      <div className="rounded-md border border-emerald-400/30 bg-emerald-500/10 px-2 py-1">
        <div className="flex items-center justify-between text-[0.68rem] text-emerald-100">
          <span className="font-semibold">
            {"\uC99D\uAC15 \uB204\uC801: \uBC31"} {whiteCount} {"\u00B7 \uD751"}{" "}
            {blackCount}
          </span>
          <span>{roundLabel}</span>
        </div>
      </div>
      <PlayerSection
        color="BLACK"
        label={"\uD751"}
        isCurrentTurn={!isWhiteTurn}
        augmentationIds={gameState.augmentations?.black?.active ?? []}
        onAugmentationClick={onAugmentationClick}
      />
      <PlayerSection
        color="WHITE"
        label={"\uBC31"}
        isCurrentTurn={isWhiteTurn}
        augmentationIds={gameState.augmentations?.white?.active ?? []}
        onAugmentationClick={onAugmentationClick}
      />
    </div>
  );
}
